import api from './api.js'

// Workforce forecast service for the "To hire or not to hire" dashboard tab.
export const workforceForecastService = {
  /**
   * Fetch the monthly workforce forecast.
   *
   * @param {Object} [params]
   * @param {string} [params.reference_month] YYYY-MM, defaults to the current month
   * @param {string} [params.scenario] low | base | high
   * @param {string} [params.forecast_mode] legacy | realistic; defaults to legacy
   * @param {string} [params.team_id] optional team filter
   * @param {string} [params.function_level] optional function level filter
   * @param {number} [params.horizon_months] 1..24
   * @returns {Promise<Object|null>} groups expose supply_fte, total_fte,
   *   max_decl_fte, fb_time_fte, verzuim_fte, verlof_fte, overig_fte and
   *   capacity_delta_fte alongside the demand series.
   */
  async getForecast(params = {}) {
    const response = await api.get('/workforce-forecast', { params })
    return response.data.success ? response.data.data : null
  },

  /**
   * Fetch the monthly FTE budget targets for one year.
   *
   * @param {number|string} year
   * @returns {Promise<Array>} rows with year, month, team and fte_target
   */
  async getFteBudgetTargets(year) {
    const response = await api.get('/workforce-forecast/fte-budgets', { params: { year } })
    return response.data.success ? response.data.data : []
  },

  /**
   * Fetch all teams and function levels available for the admin matrix.
   */
  async getFteBudgetOptions() {
    const response = await api.get('/workforce-forecast/fte-budgets/options')
    return response.data.success ? response.data.data : { teams: [], function_levels: [] }
  },

  /**
   * Upsert a monthly FTE budget target for a team and function level.
   *
   * @param {Object} target
   * @param {number|string} target.year
   * @param {number|string} target.month 1..12
   * @param {string} target.team_id
   * @param {string} target.team_name
   * @param {string} target.function_level
   * @param {number|string} target.fte_target
   * @returns {Promise<Object|null>}
   */
  async upsertFteBudgetTarget(target) {
    const response = await api.post('/workforce-forecast/fte-budgets', target)
    return response.data.success ? response.data.data : null
  },

  /**
   * Import monthly FTE targets from a spreadsheet-shaped paste in one
   * transaction. Team names are resolved server-side to environment-specific
   * Simplicate IDs.
   */
  async bulkUpsertFteBudgetTargets(year, rows) {
    const response = await api.post('/workforce-forecast/fte-budgets/bulk', { year, rows })
    return response.data.success ? response.data.data : null
  }
}
